import type { ComponentProps, JSX, ReactNode } from 'react'
import { Translation, useTranslation } from 'react-i18next'
import { Box } from '../../../components/Box.tsx'
import { Typography } from '../../../components/generic.tsx'
import { I18n } from '../../../components/I18n.tsx'
import { useDeviceRecord } from '../../../data/devices.ts'
import type { MeasurementType, QualityReason } from '../../../data/measurementTypes.ts'
import { dayjs } from '../../../dayjs.ts'

export const MeasurementTypeLabel = ({ measurementType }: { measurementType?: MeasurementType }) => {
	if (!measurementType) return null
	const { type, unit, statistic, period } = getLabel(measurementType)
	return (
		<>
			<LabelComponent label={type} />
			<LabelComponent label={statistic} separator=' ' />
			<LabelComponent label={period} separator=' - ' />
			<Typography component='span' sx={{ pl: 0.5, ':has(span:empty)': { display: 'none' } }}>
				(
				<span>
					<LabelComponent label={unit} />
				</span>
				)
			</Typography>
		</>
	)
}

export const UnitLabel = ({ measurementType, ...props }: { measurementType?: MeasurementType } & ComponentProps<typeof Typography>) => {
	const { unit } = getLabel(measurementType)
	return (
		<Typography component='span' {...props}>
			<LabelComponent label={unit} />
		</Typography>
	)
}

export const ParameterLabel = ({
	measurementType,
	includeStatistic = true,
}: {
	measurementType?: MeasurementType
	includeStatistic?: boolean
}) => {
	const { type, statistic, period } = getLabel(measurementType)
	return (
		<>
			<LabelComponent label={type} />
			{includeStatistic && <LabelComponent label={statistic} separator=' ' />}
			{includeStatistic && <LabelComponent label={period} separator=' - ' />}
		</>
	)
}

const LabelComponent = ({ label, separator }: { label: ReactNode | undefined; separator?: string }) => {
	if (!label) {
		return null
	}

	return (
		<span>
			{separator}
			{label}
		</span>
	)
}

const getLabel = (measurementType?: MeasurementType) => {
	if (!measurementType) {
		return { type: undefined, statistic: undefined }
	}

	const typePrefix = 'measurementTypeLabels'
	const unitPrefix = 'measurementUnitLabels'
	const type = <I18n i18nKey={($) => $[typePrefix][measurementType.parameter]} ns='measurementTypes' />
	const unit = <I18n i18nKey={($) => $[unitPrefix][measurementType.unit]} ns='measurementTypes' />

	if (isBaseTemperature(measurementType)) {
		const base = <I18n i18nKey={($) => $[typePrefix].SUB_SURFACE_TEMPERATURE_BASE} ns='measurementTypes' />
		return { type: base, unit }
	}

	if (isWindSpeedMaximum(measurementType)) {
		const gustType = <I18n i18nKey={($) => $[typePrefix].WIND_GUST_SPEED} ns='measurementTypes' />
		return { type: gustType, unit }
	}

	if (isStatisticalWindSpeed(measurementType) || isMinOrMaxStatistic(measurementType)) {
		const statistic = (() => {
			if (measurementType.statistic === 'MEAN' || measurementType.statistic === 'MAXIMUM' || measurementType.statistic === 'MINIMUM') {
				return (
					<I18n
						i18nKey={($) => $.measurementStatistic[measurementType.statistic as 'MEAN' | 'MAXIMUM' | 'MINIMUM']}
						ns='measurementTypes'
					/>
				)
			}
			return undefined
		})()
		return { type, unit, statistic }
	}

	if (isHourlyAccumulation(measurementType)) {
		const period = <I18n i18nKey={($) => $.measurementPeriodLabels.PT1H} ns='measurementTypes' />
		return { type, unit, period }
	}

	return { type, unit }
}

const isWindSpeedMaximum = (measurementType: Partial<MeasurementType>) => {
	return measurementType.parameter === 'WIND_SPEED' && measurementType.statistic === 'MAXIMUM'
}

const isStatisticalWindSpeed = (measurementType: Partial<MeasurementType>) => {
	const { parameter, statistic } = measurementType
	return parameter === 'WIND_SPEED' && statistic !== 'VALUE'
}

const isMinOrMaxStatistic = (measurementType: Partial<MeasurementType>) => {
	return measurementType.statistic === 'MINIMUM' || measurementType.statistic === 'MAXIMUM'
}

const hourlyAccumulationParameters = new Set(['RAIN_ACCUMULATION', 'PRECIPITATION_ACCUMULATION', 'SNOW_ACCUMULATION', 'HAIL_ACCUMULATION'])

// These parameters can report both a PT1H accumulation and a longer default period (e.g. PT24H) on the same
// device, so the PT1H one needs a suffix to distinguish it in the graph legend and table header.
export const isHourlyAccumulation = (measurementType: Partial<MeasurementType>) => {
	return (
		measurementType.statistic === 'SUM' &&
		measurementType.period === 'PT1H' &&
		hourlyAccumulationParameters.has(measurementType.parameter ?? '')
	)
}

const isBaseTemperature = ({ parameter, height }: Partial<MeasurementType>) => {
	return parameter === 'SUB_SURFACE_TEMPERATURE' && height === -0.3
}

export const QualityText = ({ reasons }: { reasons?: QualityReason[] }) => {
	const { t } = useTranslation('measurementTypes')
	const checkReasons = reasons?.map((reason) => (reason !== 'UNKNOWN' && reason !== 'TYPE' ? t(($) => $.rejected[reason]) : ''))
	return `${t(($) => $.rejected.failed)} ${(checkReasons || []).join(' ')}`
}

export const MeasurementTypeTooltipText = ({
	measurementType,
	sources,
	deviceId,
}: {
	measurementType?: MeasurementType
	sources?: string[]
	deviceId?: string
}) => {
	const { data: deviceIdRecord } = useDeviceRecord()

	if (!measurementType) {
		return null
	}
	// SUM measurements can be aggregated into multiple periods for the same parameter (e.g. PT1H and PT24H),
	// so period is included to keep the id unique across those columns.
	const periodSegment = measurementType.statistic === 'SUM' ? `-${measurementType.period}` : ''
	if (sources && sources.length > 1) {
		const sensorNames = sources.map((source) => {
			return deviceIdRecord?.[source]?.name || deviceIdRecord?.[source]?.productCode || source
		})
		return (
			<Box col flex>
				{sensorNames.map((name) => (
					<Typography
						data-ta={`measurement-tooltip-${deviceId}-${measurementType.parameter}-${measurementType.statistic}${periodSegment}`}
						key={name}
					>
						{name}
					</Typography>
				))}
			</Box>
		)
	}
	const label = formatMeasurementType(measurementType)
	return (
		<Typography data-ta={`measurement-tooltip-${deviceId}-${measurementType.parameter}-${measurementType.statistic}${periodSegment}`}>
			{label}
		</Typography>
	)
}

const formatMeasurementType = (measurementType: MeasurementType): JSX.Element => {
	const periodDuration = dayjs.duration(measurementType.period)
	const duration = periodDuration.asMinutes() > 60 ? periodDuration.asHours() : periodDuration.asMinutes()
	const durationUnit = periodDuration.asMinutes() > 60 ? 'hour' : 'minute'

	const label =
		(
			{
				MEAN: 'mean',
				VALUE: 'value',
				SUM: 'sum',
				MAXIMUM: 'max',
				MINIMUM: 'min',
			} as const
		)[measurementType.statistic] ?? 'unknown'

	return (
		<Translation>
			{(t) => (
				<I18n
					i18nKey={($) => $.measurementTypeInfo[label]}
					ns='measurementTypes'
					values={{
						duration,
						unit: t(($) => $.measurementTypeInfo[durationUnit], { ns: 'measurementTypes' }),
						height: measurementType.height,
					}}
				/>
			)}
		</Translation>
	)
}
